// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
// ==========================================================================

import React from 'react';
import { DocH2, DocH3, DocP, DocList, DocTable, DocCode } from './doc-bits.component';
import { NodeDiagram, LayersDiagram } from './diagrams.component';

/**
 * nxt, as we use it. Our build, our repositories, our hosts.
 */
const NxtDoc: React.FC = () => (
  <article className="font-body text-content-primary">
    <DocH2 id="nxt">nxt</DocH2>
    <DocP>
      The rig templates are nxt graphs, so nxt is the tool you open them in. We
      ship our own build of it, from our own repositories, packaged for each
      host we work in.
    </DocP>

    <DocH3 id="what-it-is">What it is</DocH3>
    <DocP>
      nxt is a code editor that executes as a graph. Each node holds real
      Python, and the connections between them decide what runs and in what
      order. It is not a visual scripting system that hides code behind boxes:
      you write Python, and nxt gives that Python structure, ordering, and the
      ability to be layered.
    </DocP>
    <DocP>
      The reason we build rigs in it is that a graph is a recipe, not a saved
      result. It can be re-run against an updated model, inspected step by step,
      and inherited by another graph that changes only the parts it needs to.
    </DocP>

    <NodeDiagram />

    <DocH3 id="ideas">The three ideas worth knowing</DocH3>
    <DocP>
      <strong>Nodes hold code.</strong> A node has attributes and a block of
      Python. Running the node runs the code, with its attributes available as
      tokens. Nodes sit in a hierarchy, and a child inherits from its parent.
    </DocP>
    <DocP>
      <strong>Layers compose.</strong> A graph can open on top of other graphs.
      The one on top wins where they disagree and inherits everything it does
      not mention. That is what makes a character rig a small file:
      biped_default_rig.nxt is fifteen nodes on top of roughly a thousand it
      inherits, and it is how your own asset should be built.
    </DocP>
    <LayersDiagram />

    <DocP>
      <strong>Instances share.</strong> A node can be an instance of another,
      taking its children and attributes and overriding only what it sets
      locally. The rig templates use this for left and right: right is an
      instance of left with the side attributes flipped, so anything added to
      one appears on the other without being written twice.
    </DocP>

    <DocH3 id="tokens">Tokens</DocH3>
    <DocP>
      Attribute values can refer to other values rather than repeating them:
    </DocP>
    <DocTable
      head={['Token', 'Means']}
      rows={[
        [
          <code key="a">{'${attr_name}'}</code>,
          'Another attribute on this node, or inherited by it'
        ],
        [
          <code key="b">{'${path::some/path}'}</code>,
          "A path resolved against the graph's location"
        ],
        [
          <code key="c">{'${file::some/file}'}</code>,
          'A file, found through the file roots'
        ],
        [
          <code key="d">{'${eval::expression}'}</code>,
          'The result of evaluating a Python expression'
        ]
      ]}
    />
    <DocP>
      The path and file tokens are why a template does not need to know where it
      lives. Set <code>NXT_FILE_ROOTS</code> to the folders nxt should search,
      your own location first, and a file you provide is found before the
      shipped one.
    </DocP>

    <DocH3 id="installing">Installing it</DocH3>
    <DocP>
      The download in My Products is one archive covering every host. nxt and
      the editor are the same code in all three, about 76 MB of it, and only a
      thin integration layer differs: 17 MB for Maya, 13 KB for Unreal, 44 KB
      for Blender. Splitting it per host would send the same bytes three times
      to somebody installing one of them.
    </DocP>
    <DocP>Inside are three folders. Use the one for your host:</DocP>
    <DocTable
      head={['Folder', 'Install']}
      rows={[
        [
          <code key="m">maya/</code>,
          <>
            Point <code>MAYA_MODULE_PATH</code> at it, or drop it into your Maya
            modules directory.
          </>
        ],
        [
          <code key="u">unreal/</code>,
          <>
            Drop it into your project&rsquo;s <code>Plugins</code> folder.
          </>
        ],
        [
          <code key="b">blender/</code>,
          'Install it as an addon: Edit > Preferences > Add-ons > Install.'
        ]
      ]}
    />
    <DocP>
      The framework ships its own copy of the Maya module, so if you are running
      the framework you already have nxt in Maya. This download is for the other
      hosts, and for using nxt outside the framework.
    </DocP>

    <DocH3 id="running">Running a graph</DocH3>
    <DocP>
      Open a template graph and press build; the node hierarchy executes in
      order. While you are working, individual nodes and branches can be run on
      their own, which is the normal way to iterate on a rig: build up to a
      stage, adjust something, re-run that stage rather than the whole graph.
    </DocP>
    <DocCode>{`# Running a graph without the editor, from a shell or a farm job:
python -m nxt.cli exec path/to/graph.nxt -s /some_node`}</DocCode>
  </article>
);

export default NxtDoc;
